<template>
  <BaseModal
    :title="title"
    :hookSufix="hookSufix"
    minHeight="561px"
    modalWidth="568px"
    :disableDefaultPadding="true"
    :contentClass="{
      'add-pitch-modal': true,
    }"
    :onOpenHook="handleOpen"
    :onCloseHook="handleClose"
    :persistent="loading"
  >
    <template #subtitle>
      <p
        class="body-regular-smaller neutral-40-light--text text-wrap mb-0 mt-1"
      >
        {{ subtitle }}
        <base-btn
          type="neutral"
          size="xsmall"
          class="px-1"
          @click="openHelpArticle"
        >
          {{ $t('message.pages.analyticsV3.pages.devices.learnMore') }}
          <v-icon size="16" style="margin-left: 2px">mdi-open-in-new</v-icon>
        </base-btn>
      </p>
    </template>
    <template #content>
      <div
        class="iframe mt-6 d-flex justify-center"
        style="position: relative; padding-top: 46.25%"
        v-if="videoEmbed"
      >
        <iframe
          id="panda-player"
          :src="`${videoEmbed}&isDashboard=true`"
          style="
            border: none;
            position: absolute;
            top: 0;
            width: 100%;
            height: 100%;
          "
        >
        </iframe>
      </div>
      <v-row no-gutters class="mt-12">
        <v-col cols="12" v-if="showNameInput">
          <v-input
            :label="
              $i18next.t(
                'message.pages.analyticsV3.retentionv3.pitches.custom.inputs.name.label'
              )
            "
            :placeholder="
              $i18next.t(
                'message.pages.analyticsV3.retentionv3.pitches.custom.inputs.name.placeholder'
              )
            "
            v-model="modalData.nameInput"
            class="time-input"
            maxlength="30"
            :counter="30"
            :rules="[(v) => v.length <= 30]"
          ></v-input
        ></v-col>
        <v-col>
          <v-input
            :label="
              $i18next.t(
                'message.pages.analyticsV3.retentionv3.pitches.pitch.inputs.timeAbsolute.label'
              )
            "
            :placeholder="isVideoLongerThanOneHour ? '00:00:00' : '00:00'"
            v-model="modalData.timeInput"
            :hideDetails="!timeInputError.state"
            class="time-input"
            :maxlength="isVideoLongerThanOneHour ? 8 : 5"
            @keydown="onKeyDownInput"
            :rules="[
              (v) =>
                getTimeInSeconds(v) > timeVideo ? timeInputError.message : true,
            ]"
          ></v-input
        ></v-col>
        <v-col class="pl-6">
          <v-input
            :label="
              $i18next.t(
                'message.pages.analyticsV3.retentionv3.pitches.pitch.inputs.timePercentage.label'
              )
            "
            placeholder="0%"
            v-model="modalData.percentageInput"
            :hideDetails="true"
            class="time-input"
            :maxlength="3"
            @keydown="onKeyDownInputPercentage"
          ></v-input
        ></v-col>
      </v-row>
    </template>
    <template #card-actions>
      <div
        class="d-flex justify-end py-6 px-6"
        style="border-top: 1px solid var(--v-neutral-95-lightest-base)"
      >
        <base-btn
          type="tertiary"
          class="mr-2"
          :disabled="loading"
          @click="callClose"
          >{{ $t('message.cancel') }}</base-btn
        >
        <v-tooltip
          top
          :disabled="
            modalData.timeInput.length > 0 &&
            modalData.percentageInput.length > 0
          "
        >
          <template #activator="{ on, attrs }">
            <base-btn
              type="primary"
              @click="emitValues"
              :loading="loading"
              :disabled="
                loading ||
                !modalData.timeInput ||
                !modalData.percentageInput ||
                !hasChanges ||
                timeInputError.state
              "
              v-bind="attrs"
              v-on="on"
              >{{ $t('message.pages.analyticsV3.retentionv3.add') }}
            </base-btn>
          </template>
          <span>
            {{
              $t('message.pages.analyticsV3.retentionv3.completeAboveInputs')
            }}
          </span>
        </v-tooltip>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/BaseModal.vue'
import _ from 'lodash'

export default {
  name: 'BaseRetentionModal',
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    hookSufix: {
      type: String,
      required: true,
    },
    helpArticle: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showNameInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalData: {
        timeInput: '',
        percentageInput: '',
        nameInput: this.$i18next.t(
          'message.pages.analyticsV3.retentionv3.pitches.custom.inputs.name.placeholder'
        ),
      },
      timeInputError: {
        state: false,
        message: this.$i18next.t(
          'message.pages.analyticsV3.retentionv3.pitches.errorMessage'
        ),
      },
      initialData: {},
      video: {},
    }
  },
  components: {
    BaseModal,
  },
  computed: {
    hasChanges() {
      return !_.isEqual(this.modalData, this.initialData)
    },
    timeVideo() {
      return this.video?.video?.duration ?? 0
    },
    isVideoLongerThanOneHour() {
      return this.timeVideo >= 3600
    },
    videoEmbed() {
      return this.video?.video?.hls
        ? this.convertHlsToEmbed(this.video.video.hls)
        : ''
    },
  },
  methods: {
    openHelpArticle() {
      window.open(this.helpArticle, '_blank')
    },
    getVideoExternalIdFromHls(hls) {
      const match = hls.match(
        /https:\/\/b-(.+?)\.tv\.pandavideo\.com\.br\/(.+?)\/playlist\.m3u8/
      )

      if (!match) {
        throw new Error('URL inválida ou em formato inesperado.')
      }

      return match[2]
    },
    convertHlsToEmbed(hls) {
      const match = hls.match(
        /https:\/\/b-(.+?)\.tv\.pandavideo\.com\.br\/(.+?)\/playlist\.m3u8/
      )

      if (!match) {
        throw new Error('URL inválida ou em formato inesperado.')
      }

      const subdomain = match[1] // ex: vz-ded14ebd-85a
      const videoId = match[2] // ex: b7f538d3-40dc-45b2-9c41-24a022fb70ee

      return `https://player-${subdomain}.tv.pandavideo.com${
        hls.includes('.com.br') ? '.br' : ''
      }/embed/?v=${videoId}`
    },
    async getVideoIdFromIndexedDB() {
      const videoExternalId = this.getVideoExternalIdFromHls(
        this.video.video.hls
      )
      let videoId = null
      if (videoExternalId) {
        try {
          if (!window.db) {
            this.$root.$emit('initDexie')
          }
          const videoFromDB = await window.db.videos
            .where({ video_external_id: videoExternalId })
            .first()
          videoId = videoFromDB?.id
          return videoId
        } catch (error) {
          console.error('Error getting video from IndexedDB:', error)
          return null
        }
      }
      return null
    },
    async emitValues() {
      const videoId = await this.getVideoIdFromIndexedDB()

      this.$emit('ok', {
        time: this.getTimeInSeconds(this.modalData.timeInput),
        video_id: videoId,
        video: this.video,
        title: this.modalData.nameInput,
      })
    },
    getTimeInSeconds(time) {
      const timeParts = time.split(':').map(Number)
      if (timeParts.length === 3) {
        return timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2]
      }
      return timeParts[0] * 60 + timeParts[1]
    },
    handleOpen(video) {
      const key =
        this.hookSufix === 'AddPitchModal'
          ? 'pitchRetention'
          : 'customRetention'

      if (video[key]) {
        this.modalData.timeInput = this.parseSecondsToTime(video[key].time)
        if (video[key].title) {
          this.modalData.nameInput = video[key].title
        }
        this.$nextTick(() => {
          this.syncPercentageFromTime()
          this.initialData = _.cloneDeep(this.modalData)
        })
      } else {
        this.modalData.percentageInput = ''
        this.modalData.timeInput = ''
      }
      this.initialData = _.cloneDeep(this.modalData)
      this.video = video
    },
    callClose() {
      this.$root.$emit(`close${this.hookSufix}`)
    },
    handleClose() {
      this.modalData.timeInput = ''
      this.modalData.percentageInput = ''
      this.video = {}
    },
    parseSecondsToTime(sec) {
      const hours = Math.floor(sec / 3600)
      const minutes = Math.floor((sec % 3600) / 60)
      const seconds = sec % 60
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    onKeyDownInput(event) {
      const allowedKeys = [
        'Backspace',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Delete',
      ]

      if (allowedKeys.includes(event.key)) {
        return
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
        return
      }

      let numbers = event.target.value.replace(/\D/g, '')
      if (event.key === 'Backspace') {
        numbers = numbers.slice(0, -1)
      } else {
        numbers += event.key
      }

      let parsedTime
      if (this.timeVideo >= 3600) {
        numbers = numbers.padStart(6, '0').slice(-6)

        const hours = numbers.slice(0, 2)
        const minutes = numbers.slice(2, 4)
        const seconds = numbers.slice(4, 6)

        parsedTime = `${hours}:${minutes}:${seconds}`
      } else {
        numbers = numbers.padStart(4, '0').slice(-4)

        const minutes = numbers.slice(0, 2)
        const seconds = numbers.slice(2, 4)

        parsedTime = `${minutes}:${seconds}`
      }
      const timeInSeconds = this.getTimeInSeconds(parsedTime)
      this.timeInputError.state = timeInSeconds > this.timeVideo
      this.modalData.timeInput = parsedTime
      this.syncPercentageFromTime()

      event.preventDefault()
    },
    onKeyDownInputPercentage(event) {
      const allowedKeys = [
        'Backspace',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Delete',
      ]

      if (allowedKeys.includes(event.key)) {
        return
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault()
        return
      }

      let numbers = event.target.value.replace(/\D/g, '')
      if (event.key === 'Backspace') {
        numbers = numbers.slice(0, -1)
      } else {
        numbers += event.key
      }

      if (parseInt(numbers) > 100) {
        event.preventDefault()
        return
      }

      this.modalData.percentageInput = `${numbers}%`

      // Sync time when percentage changes
      this.syncTimeFromPercentage()

      event.preventDefault()
    },
    syncTimeFromPercentage() {
      if (!this.modalData.percentageInput || !this.timeVideo) return

      const percentage = parseInt(
        this.modalData.percentageInput.replace('%', '')
      )
      const timeInSeconds = Math.round((percentage / 100) * this.timeVideo)

      this.modalData.timeInput = this.formatTimeFromSeconds(timeInSeconds)
    },
    syncPercentageFromTime() {
      if (!this.modalData.timeInput || !this.timeVideo) return

      const timeInSeconds = this.getTimeInSeconds(this.modalData.timeInput)
      const percentage = Math.round((timeInSeconds / this.timeVideo) * 100)

      this.modalData.percentageInput = `${percentage}%`
    },
    formatTimeFromSeconds(seconds) {
      if (this.timeVideo >= 3600) {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = seconds % 60
        return `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      } else {
        const minutes = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${minutes.toString().padStart(2, '0')}:${secs
          .toString()
          .padStart(2, '0')}`
      }
    },
  },
}
</script>

<style lang="scss">
.add-pitch-modal {
  .v-toolbar__content {
    padding: 0 8px !important;
  }

  .modal-content {
    padding: 24px 16px !important;
  }
  .time-input {
    .v-label {
      left: -10px !important;
    }
  }
}

.player-container {
  width: calc(552px - 16px);
  height: 281px;
  background-color: rgb(68, 68, 147);
  display: block;
}
</style>
